import axios from "axios";
import { useEffect, useState } from "react";
import {RequestError} from "../utils/apiError";
import {toRequestError} from "../utils/apiError";


// Runs a GET-style request with loading/error state and automatic cancellation.
// Pass a function with a STABLE identity (module-level, or wrapped in useCallback):
// the request re-runs whenever that function changes. Pass null to skip the request.
export const useRequest = <T>(fetcher: ((signal: AbortSignal) => Promise<T>) | null) => {
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState(fetcher !== null);
    const [error, setError] = useState<RequestError | null>(null);

    useEffect(() => {
        if (!fetcher) {
            setLoading(false);
            return;
        }

        const controller = new AbortController();

        // Clear the previous result so stale data never flashes
        setData(null);
        setError(null);
        setLoading(true);

        fetcher(controller.signal)
            .then(setData)
            .catch((err) => {
                if (axios.isCancel(err)) return; // Page was left: not a real error
                console.error("Request failed:", err);
                setError(toRequestError(err));
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => controller.abort();
    }, [fetcher]);

    return { data, loading, error };
};